// @flow

import http from "@http";
import config from "@config";
import type { HTTPRequestResponseType } from "@types";

import { all, put, takeEvery, call } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import { route } from "@routes/helper";
import actionCreators from "@modules/service/actionCreator";
import {
  GetRtw,
  GetRtwSchema,
  AddRtw,
  StartDraftRtw,
  UpdateRtw,
  UpdateRtwAssignments,
  DisableRtw,
  GetRtwProcesses,
} from "@modules/service/constants/actions";
import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import type { Rtw, RTWFilterStateType } from "@modules/service/reducer/rtw/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ItemResponse<T> = $Exact<T>;

// READ

type RtwListRequestType = $Exact<{
  clientId: string,
  page?: number,
  isActive?: RTWFilterStateType,
}>;

export function* onGetRtwProcesses({
  payload,
}: Action<RtwListRequestType>): Generator<*, void, HTTPRequestResponseType> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process`,
      {
        params: {
          page: payload.page || 1,
          limit: config.resultsPerPage,
          is_active: payload.isActive,
        },
      },
    );

    yield put(actionCreators.GetRtwProcessesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetRtwProcessesError(e.response));
  }
}

type RtwRequest = RtwListRequestType &
  $Exact<{
    processId: string,
  }>;

export function* onGetRtw({
  payload,
}: Action<RtwRequest>): Generator<*, void, { data: ItemResponse<ApprovableContainer<Rtw>> }> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const stableResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/assignment`,
    );

    yield all([
      put(actionCreators.GetRtwSuccess(stableResponse.data)),
      put(actionCreators.SetRtwAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetRtwError(e.response));
  }
}

type RefreshRequest = $Exact<{
  clientId: string,
  processId?: string,
  isActive?: boolean,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  yield put(
    actionCreators.GetRtwProcesses({ clientId: params.clientId, isActive: params.isActive }),
  );

  if (params.processId) {
    yield put(
      actionCreators.GetRtw({
        clientId: params.clientId,
        processId: params.processId,
        isActive: params.isActive,
      }),
    );
  }
}

// CREATE

type GetRtwSchemaRequest = $Exact<{
  clientId?: number,
}>;

export function* onGetRtwSchema({
  payload,
}: Action<GetRtwSchemaRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("RtwInterviewProcessRevisionDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetRtwSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetRtwSchemaError(e.response));
  }
}

export function* onStartDraftRtw({
  payload,
}: Action<RtwRequest>): Generator<*, void, { data: ItemResponse<ApprovableContainer<Rtw>> }> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}`,
    );

    yield put(actionCreators.StartDraftRtwSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.StartDraftRtwError(e.response));
  }
}

type RtwAddRequest = RtwListRequestType &
  $Exact<{
    draft: Rtw,
    assignments: Array<HierarchyAssignment>,
  }>;

export function* onAddRtw({ payload }: Action<RtwAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const draft = {
      ...payload.draft.content,
    };
    delete draft.id;

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process`,
      draft,
    );
    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, { clientId: payload.clientId, isActive: payload.isActive });

    yield all([
      put(actionCreators.AddRtwSuccess(response.data)),
      put(sendSuccessMessage("DRAFT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddRtwError(e.response));
  }
}

// UPDATE

type RtwUpdateRequest = RtwRequest &
  $Exact<{
    draft: Rtw,
  }>;

export function* onUpdateRtw({ payload }: Action<RtwUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const draft = {
      ...payload.draft.content,
    };
    delete draft.id;

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}`,
      draft,
    );

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.UpdateRtwSuccess(response.data)),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateRtwError(e.response));
  }
}

type RtwUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<Rtw>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateRtwAssignments({
  payload,
}: Action<RtwUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/rtw-interview/process/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/rtw-interview/process/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/rtw-interview/process/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      processId: payload.container.id,
    });
  } catch (e) {
    yield put(actionCreators.UpdateRtwAssignmentsError(e.response));
  }
}

// DISABLE

export type RtwDisableRequest = RtwRequest &
  $Exact<{
    approvable: ApprovableContainer<Rtw>,
  }>;

export function* onDisable({
  payload,
}: Action<RtwDisableRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Rtw>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.approvable) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const draft = payload.approvable;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}`,
      draft,
    );

    yield all([
      put(actionCreators.DisableRtwSuccess(response.data)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_FEATURES_RTW_FORM", {
            clientId: payload.clientId,
          }),
        ),
      ),
    ]);
    yield put(sendSuccessMessage("DRAFT_CREATED"));
  } catch (e) {
    yield put(actionCreators.DisableRtwError(e.response));
  }
}

// ROOT

export function* RtwSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetRtwProcesses, onGetRtwProcesses),
    takeEvery(GetRtw, onGetRtw),
    takeEvery(GetRtwSchema, onGetRtwSchema),
    takeEvery(AddRtw, onAddRtw),
    takeEvery(StartDraftRtw, onStartDraftRtw),
    takeEvery(UpdateRtw, onUpdateRtw),
    takeEvery(UpdateRtwAssignments, onUpdateRtwAssignments),
    takeEvery(DisableRtw, onDisable),
  ]);
}

export default RtwSagas;
